import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '../../utilities/debounce';

/**
 * One marker per group of choices that travels to whichever tile is selected,
 * instead of each tile lighting up where it stands.
 *
 * This is `.nav-fill` from the sidebar (see Sidebar.jsx), brought to the
 * checkout because it is the same idea: a single highlight that slides to its
 * new home, so the eye follows the selection rather than re-finding it. The
 * geometry is measured for the same reason it is there — these grids re-flow
 * (`auto-fit` collapses tracks, `--ta-cols` differs per group, and the option
 * list itself changes when you pick another Step), so an index and a fixed
 * tile width like the dock's could not place it.
 *
 * Purely presentational: it reads the DOM and returns a decorative `<span>`.
 * It owns no selection state and never calls back into the page — the tile
 * that is `aria-pressed` is already the answer, so nothing here needs to know
 * how a choice is made or stored.
 *
 * Only for single-select groups. Add-ons toggles several at once, and one
 * marker cannot be in two places — that group keeps the per-tile crossfade.
 *
 * @param selected  the chosen option's id; a change to it slides the marker
 * @param layout    anything that changes the *set* of tiles without changing
 *                  the grid's size — the option list's length. A change here
 *                  re-places the marker instead of sliding it.
 */
export default function useSelectionFill(selected, layout) {
  const box = useRef(null);
  const observer = useRef(null);
  const [fill, setFill] = useState(null);

  /* What separates the two kinds of change, exactly as in Sidebar.jsx. Picking
     another tile leaves the grid where it is, so the marker eases across. A
     new option list, or a re-flow, moves the tiles themselves — easing to a
     target that is still moving is what reads as drift, so the marker is
     placed outright and only earns its transition once things settle. */
  const [smooth, setSmooth] = useState(false);

  /* Measured from rects against the grid's own box rather than `offsetLeft`:
     a tile's offset parent is whatever happens to be positioned above it, and
     mid-reflow rects give the position the tile is actually at. */
  const measure = useCallback(() => {
    const grid = box.current;
    // The selected tile already identifies itself for screen readers, so the
    // marker rides on that rather than on a second, parallel marker attribute
    // that could drift out of step with it.
    const tile = grid?.querySelector('[aria-pressed="true"]');

    if (!grid || !tile) {
      setFill(null);
      return;
    }

    const gridRect = grid.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();
    const next = {
      left: tileRect.left - gridRect.left,
      top: tileRect.top - gridRect.top,
      width: tileRect.width,
      height: tileRect.height,
    };

    // A ResizeObserver fires on every re-flow and most of them leave the tile
    // where it was — bailing on an unchanged rect keeps that from re-rendering
    // the page for nothing.
    setFill((prev) =>
      prev &&
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next,
    );
  }, []);

  /* A callback ref, not `useRef` — half of these grids are not in the tree on
     first render. Payment and Spread only exist at stage 2, and the step-1
     grids are gone by then, so an effect keyed on the selection would have
     nothing to measure when it ran and would never run again: the marker would
     simply never appear on those groups. A callback ref fires on the commit
     that attaches the node, whenever that happens.

     It also owns the ResizeObserver, for the same reason — one set up against
     a ref that was still null would never observe anything. */
  const ref = useCallback(
    (node) => {
      observer.current?.disconnect();
      observer.current = null;
      box.current = node;

      if (!node) {
        setFill(null);
        return;
      }

      // Placed, not slid: there is no previous position to travel from.
      setSmooth(false);
      measure();

      if (typeof ResizeObserver === 'undefined') return;

      /* A window resize re-flows the grid under the marker — `auto-fit` drops
         a column and every tile moves at once. Following that with the
         transition live would leave the marker chasing tiles that have already
         gone, so it tracks them directly and takes its easing back once the
         resize stops. The first callback fires on `observe`, which is also
         what grants the marker its transition after the initial placement. */
      const settle = debounce(() => setSmooth(true), 150);
      const resize = new ResizeObserver(() => {
        setSmooth(false);
        measure();
        settle();
      });

      resize.observe(node);
      observer.current = resize;
    },
    [measure],
  );

  // Before paint, so the marker is never seen at a stale position.
  useLayoutEffect(measure, [measure, selected, layout]);

  /* A different set of tiles in the same-sized grid — swapping Step re-defaults
     Account Type and Size — moves the selection without moving the container,
     so the ResizeObserver above never hears about it. The marker has to be
     placed on the new tile rather than travelling across a grid that is no
     longer the one it started in.

     rAF rather than a timer: this is waiting for a frame, which is what rAF
     means. It is throttled to a standstill in a background tab, but the cost
     there is only that the marker stays un-animated until the tab is looked
     at — `measure` has already run, so it is in the right place regardless. */
  useLayoutEffect(() => {
    setSmooth(false);
    const frame = requestAnimationFrame(() => setSmooth(true));
    return () => cancelAnimationFrame(frame);
  }, [layout]);

  const node = useMemo(
    () =>
      fill ? (
        <span
          className={`ta-fill${smooth ? ' is-smooth' : ''}`}
          style={{
            transform: `translate3d(${fill.left}px, ${fill.top}px, 0)`,
            width: `${fill.width}px`,
            height: `${fill.height}px`,
          }}
          // Decorative. Which tile is chosen is already in the accessibility
          // tree, on the tile, as `aria-pressed`.
          aria-hidden="true"
        />
      ) : null,
    [fill, smooth],
  );

  return {
    /** Goes on the grid that holds the tiles. */
    ref,
    /** Appended to the grid's class; switches off the per-tile crossfade. */
    className: fill ? ' has-fill' : '',
    /** Rendered as the grid's first child, under the tiles' content. */
    node,
  };
}
